import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Menu, MenuList } from '@material-ui/core';
import { isTestMode } from '../../utils/test-mode';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { useDevice } from '../Device';
import { useIntentBehavior } from '../IntentBehavior';
import { Box, Row } from '../Layout';
import { MenuItem } from '../MenuItem/MenuItem';
import { useOverlayContext } from '../OverlayContext';
import { Tooltip } from '../Tooltip';
import { useDropdownMenuStyles } from './DropdownMenu.styles';
import { DropdownMenuItemTestIds } from './DropdownMenu.utils';
export function DropdownMenu(props) {
    const { isMobileDevice } = useDevice();
    const Component = isMobileDevice ? DropdownMenuMobile : DropdownMenuDesktop;
    return /*#__PURE__*/ _jsx(Component, _object_spread({}, props));
}
export function DropdownMenuMobile(props) {
    const { target, onOpenChange, children } = props;
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const clonedTarget = /*#__PURE__*/ React.cloneElement(target, {
        onClick: (event)=>{
            pushBottomSheetPage({
                content: /*#__PURE__*/ _jsx(MenuList, {
                    disablePadding: true,
                    children: clonedChildren
                }),
                onClose: ()=>onOpenChange && onOpenChange(false)
            });
            onOpenChange && onOpenChange(true);
            target.props.onClick && target.props.onClick(event);
        }
    });
    const clonedChildren = React.Children.map(children, (child)=>{
        const rawChild = child;
        if (!rawChild) {
            return rawChild;
        }
        return rawChild.props.closeOnClick ? /*#__PURE__*/ React.cloneElement(rawChild, {
            onClick: (event, biContext)=>{
                popBottomSheetPage();
                rawChild.props.onClick && rawChild.props.onClick(event, biContext);
            }
        }) : rawChild;
    });
    return clonedTarget;
}
export function DropdownMenuDesktop(props) {
    const { target, children, hAlign = 'right', vAlign = 'bottom', onOpenChange, onClose, id, promotionalAction, promotionalActionOnClick } = props;
    const overrides = useDropdownMenuStyles({
        minWidth: props.minWidth,
        maxHeight: props.maxHeight,
        maxWidth: props.maxWidth,
        width: props.width
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const menuId = useMemo(()=>id || Math.random().toString(), [
        id
    ]);
    const clonedTarget = /*#__PURE__*/ React.cloneElement(target, {
        'aria-owns': anchorEl ? menuId : undefined,
        'aria-haspopup': 'true',
        onClick: (event)=>{
            setAnchorEl(event.currentTarget);
            onOpenChange && onOpenChange(true);
            target.props.onClick && target.props.onClick(event);
        }
    });
    const clonedChildren = React.Children.map(children, (child)=>{
        const rawChild = child;
        if (!rawChild) {
            return rawChild;
        }
        return rawChild.props.closeOnClick ? /*#__PURE__*/ React.cloneElement(rawChild, {
            onClick: (event, biContext)=>{
                setAnchorEl(null);
                rawChild.props.onClick && rawChild.props.onClick(event, biContext);
            }
        }) : rawChild;
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            clonedTarget,
            /*#__PURE__*/ _jsxs(Menu, {
                classes: {
                    paper: overrides.menu
                },
                id: menuId,
                anchorEl: anchorEl,
                open: Boolean(anchorEl),
                autoFocus: false,
                onClose: (event, reason)=>{
                    onClose && onClose(anchorEl, reason, event);
                    setAnchorEl(null);
                },
                TransitionProps: {
                    onExited () {
                        onOpenChange && onOpenChange(false);
                    }
                },
                getContentAnchorEl: null,
                transitionDuration: isTestMode ? 0 : 'auto',
                anchorOrigin: {
                    vertical: vAlign === 'top' ? -10 : 'bottom',
                    horizontal: hAlign
                },
                transformOrigin: {
                    vertical: vAlign === 'bottom' ? -10 : 'bottom',
                    horizontal: hAlign
                },
                "data-testid": props.testId,
                children: [
                    clonedChildren,
                    promotionalAction && /*#__PURE__*/ _jsx(Box, {
                        onClick: promotionalActionOnClick,
                        className: overrides.promotionalAction,
                        children: promotionalAction
                    })
                ]
            })
        ]
    });
}
export const DropdownMenuItem = /*#__PURE__*/ React.forwardRef(function DropdownMenuItem(props, ref) {
    const { children, iconStart, iconEnd, skin = 'secondary', as, disabled, intent, tooltipProps, closeOnClick, onClick, testId } = props, rest = _object_without_properties(props, [
        "children",
        "iconStart",
        "iconEnd",
        "skin",
        "as",
        "disabled",
        "intent",
        "tooltipProps",
        "closeOnClick",
        "onClick",
        "testId"
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const isDisabled = disabledFromIntent || disabled;
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: !!(tooltipProps === null || tooltipProps === void 0 ? void 0 : tooltipProps.title) || !!tooltipFromIntent,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({
                testId: DropdownMenuItemTestIds.Tooltip(testId !== null && testId !== void 0 ? testId : '')
            }, tooltipProps || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: isDisabled ? /*#__PURE__*/ _jsx("div", {
                    "data-testid": DropdownMenuItemTestIds.Wrapper(testId !== null && testId !== void 0 ? testId : ''),
                    children: children
                }) : children
            })),
        children: /*#__PURE__*/ _jsx(MenuItem, _object_spread_props(_object_spread({}, rest), {
            onClick: (e, biContext)=>onClick === null || onClick === void 0 ? void 0 : onClick(e, biContext),
            disabled: isDisabled,
            component: as,
            skin: skin,
            ref: ref,
            testId: testId,
            children: /*#__PURE__*/ _jsxs(Row, {
                gap: 12,
                alignItems: "center",
                children: [
                    iconStart,
                    /*#__PURE__*/ _jsx("span", {
                        children: children
                    }),
                    iconEnd
                ]
            })
        }))
    });
});
DropdownMenuItem.defaultProps = {
    closeOnClick: true
};
DropdownMenu.Item = DropdownMenuItem;
